import type { NotificationProps } from '../../../types/models/notifications';
import type { BackupVmMapping } from '../../../types/models/BackupVmMapping';

export type AttributionNotification = Omit<NotificationProps, 'key' | 'timestamp' | 'isNotificationRead'>;

/**
 * The feedback for a saved attribution, in ONE place — because the rule it encodes is easy to get
 * subtly wrong in a second copy.
 *
 * `subject_restamped` is the backend's affected-rows count. A **0 on create** means the triple
 * matched no reported backup: either a deliberate pre-seed, or a typo in the hypervisor/VM name
 * announcing itself. Reporting that as a plain success would hide the typo behind a green tick,
 * so it is a WARNING that names the triple and says what will happen if the directory shows up.
 *
 * Used by both the create/edit modal and the queue's one-click attribute action.
 */
export function attributionSavedNotification(
  saved: BackupVmMapping,
  subjectRestamped: number,
  isEdit: boolean,
): AttributionNotification {
  if (!isEdit && subjectRestamped === 0) {
    return {
      title: 'Attribution saved, but nothing matched',
      srTitle: 'Warning alert',
      description: `Saved, but no backup currently matches ${saved.hypervisor}/${saved.vm_name}. Check the names — the attribution will apply if and when that directory is reported.`,
      variant: 'warning',
    };
  }
  return {
    title: isEdit ? 'Attribution updated' : 'Virtual machine attributed',
    srTitle: 'Success alert',
    description: `${saved.vm_name} on ${saved.hypervisor} is now attributed to ${saved.client_name ?? `client #${saved.clientID}`}.`,
    variant: 'success',
  };
}
